import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Users, Send, ArrowLeft, MessageSquare, Crown, Shield, Link2, Camera, Paperclip, FileText, X, Image } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GroupChatsPage({ user }) {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchChats();
  }, []);

  // Auto-select chat from URL param
  useEffect(() => {
    const chatId = searchParams.get('chat');
    if (chatId && chats.length > 0 && !selectedChat) {
      const found = chats.find(c => c.id === chatId);
      if (found) {
        setSelectedChat(found);
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats, searchParams]);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages();
      fetchMembers();
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat]);

  // Removed auto-scroll - users prefer to stay where they are

  const fetchChats = async () => {
    try {
      const response = await axios.get(`${API}/group-chats`);
      setChats(response.data);
    } catch (error) {
      toast.error('Failed to load group chats');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!selectedChat) return;
    try {
      const response = await axios.get(`${API}/group-chats/${selectedChat.id}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const fetchMembers = async () => {
    if (!selectedChat) return;
    try {
      const response = await axios.get(`${API}/group-chats/${selectedChat.id}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to load members');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && attachments.length === 0) || !selectedChat) return;

    setSending(true);
    try {
      await axios.post(`${API}/group-chats/${selectedChat.id}/messages`, {
        content: newMessage,
        attachments: attachments.map(a => JSON.stringify({ url: a.url, filename: a.filename, content_type: a.content_type, is_image: a.is_image }))
      });
      setNewMessage('');
      setAttachments([]);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleAttachFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (attachments.length + files.length > 5) {
      toast.error('Maximum 5 attachments per message');
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload/chat-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setAttachments(prev => [...prev, res.data]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getChatIcon = (chat, size = 'w-5 h-5') => {
    if (chat.image) {
      return <img src={getImageUrl(chat.image)} alt={chat.name} className="w-full h-full rounded-full object-cover" />;
    }
    if (chat.type === 'leaders') return <Crown className={`${size} text-[#cb9944]`} />;
    if (chat.type === 'admins') return <Shield className={`${size} text-[#cb9944]`} />;
    return <Users className={`${size} text-[#5b7f95]`} />;
  };

  const canChangeIcon = (chat) => {
    if (user.is_admin) return true;
    if (user.is_area_manager && chat.type === 'chapter') {
      const managed = [...(user.managed_areas || [])];
      if (user.managed_area && !managed.includes(user.managed_area)) managed.push(user.managed_area);
      if (user.area && !managed.includes(user.area)) managed.push(user.area);
      const chatChapter = chat.id.replace('chapter_', '');
      return managed.includes(chatChapter);
    }
    return false;
  };

  const handleChatImageUpload = async (e, chatId) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/group-chats/${chatId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Chat image updated!');
      fetchChats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update image');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-[#64748b]">Loading group chats...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6" data-testid="group-chats-page">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/messages">
          <Button variant="ghost" size="sm" className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Direct Messages
          </Button>
        </Link>
        <h1 className="text-3xl font-bold text-[#2d3748]">Group Chats</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat List */}
        <div className="lg:col-span-1">
          <Card className="border-gray-100 rounded-2xl shadow-sm">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#5b7f95]" />
                Your Groups
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {chats.length === 0 ? (
                <div className="p-6 text-center text-[#64748b]">
                  No group chats available
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {chats.map((chat) => (
                    <div key={chat.id} className={`flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors ${
                      selectedChat?.id === chat.id ? 'bg-[#5b7f95]/10' : ''
                    }`}>
                      <button
                        onClick={() => setSelectedChat(chat)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                        data-testid="group-chat-item"
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${
                          chat.image ? '' : chat.type === 'leaders' ? 'bg-[#cb9944]/20' : 'bg-[#5b7f95]/20'
                        }`}>
                          {getChatIcon(chat)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-[#2d3748] truncate">{chat.name}</h3>
                          <p className="text-xs text-[#64748b] truncate">{chat.description}</p>
                        </div>
                      </button>
                      {canChangeIcon(chat) && (
                        <label className="cursor-pointer flex-shrink-0" title="Change chat image">
                          <Camera className="w-4 h-4 text-[#94a3b8] hover:text-[#5b7f95] transition-colors" />
                          <input type="file" accept="image/*" onChange={(e) => handleChatImageUpload(e, chat.id)} className="hidden" />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Members List (when chat is selected) */}
          {selectedChat && members.length > 0 && (
            <Card className="border-gray-100 rounded-2xl shadow-sm mt-4">
              <CardHeader className="border-b border-gray-100 py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#5b7f95]" />
                  Members ({members.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-48">
                  <div className="divide-y divide-gray-50">
                    {members.map((member) => (
                      <div key={member.id} className="p-3 flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={member.profile_image ? getImageUrl(member.profile_image) : undefined} />
                          <AvatarFallback className="bg-[#5b7f95] text-white text-xs">
                            {(member.display_name || member.full_name).charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#2d3748] truncate">
                            {member.display_name || member.full_name}
                            {member.is_area_manager && (
                              <Crown className="w-3 h-3 inline ml-1 text-[#cb9944]" />
                            )}
                            {member.is_admin && (
                              <Shield className="w-3 h-3 inline ml-1 text-[#5b7f95]" />
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Chat Messages */}
        <div className="lg:col-span-2">
          <Card className="border-gray-100 rounded-2xl shadow-sm h-[600px] flex flex-col">
            {selectedChat ? (
              <>
                <CardHeader className="border-b border-gray-100 py-4">
                  <CardTitle className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ${
                      selectedChat.image ? '' : selectedChat.type === 'leaders' ? 'bg-[#cb9944]/20' : 'bg-[#5b7f95]/20'
                    }`}>
                      {getChatIcon(selectedChat)}
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-[#2d3748]">{selectedChat.name}</h2>
                      <p className="text-xs text-[#64748b]">{members.length} members</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full border-[#5b7f95] text-[#5b7f95] hover:bg-[#5b7f95] hover:text-white"
                      onClick={() => {
                        const url = `${window.location.origin}/group-chats?chat=${selectedChat.id}`;
                        navigator.clipboard.writeText(url);
                        toast.success('Chat link copied!');
                      }}
                      data-testid="copy-chat-link-btn"
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      Copy Link
                    </Button>
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="flex-1 overflow-hidden p-0">
                  <ScrollArea className="h-full p-4">
                    {messages.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-[#64748b]">
                        <div className="text-center">
                          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p>No messages yet. Start the conversation!</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {messages.map((message) => {
                          const isOwnMessage = message.sender_id === user.id;
                          return (
                            <div
                              key={message.id}
                              className={`flex gap-3 ${isOwnMessage ? 'flex-row-reverse' : ''}`}
                            >
                              <Avatar className="h-8 w-8 flex-shrink-0">
                                <AvatarImage src={message.sender_profile_image ? getImageUrl(message.sender_profile_image) : undefined} />
                                <AvatarFallback className={`${isOwnMessage ? 'bg-[#cb9944]' : 'bg-[#5b7f95]'} text-white text-xs`}>
                                  {message.sender_name.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div className={`max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                                <div className={`flex items-center gap-2 mb-1 ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
                                  <span className="text-xs font-medium text-[#2d3748]">
                                    {isOwnMessage ? 'You' : message.sender_name}
                                  </span>
                                  <span className="text-xs text-[#64748b]">
                                    {format(new Date(message.created_at), 'MMM d, h:mm a')}
                                  </span>
                                </div>
                                <div className={`rounded-2xl px-4 py-2 ${
                                  isOwnMessage 
                                    ? 'bg-[#5b7f95] text-white rounded-tr-none' 
                                    : 'bg-gray-100 text-[#2d3748] rounded-tl-none'
                                }`}>
                                  {message.content && <p className="text-sm whitespace-pre-wrap">{message.content}</p>}
                                  {message.attachments && message.attachments.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                      {message.attachments.map((att, i) => {
                                        let parsed;
                                        try { parsed = typeof att === 'string' ? JSON.parse(att) : att; } catch { parsed = { url: att, filename: 'file' }; }
                                        if (parsed.is_image) {
                                          return (
                                            <a key={`att-${i}`} href={parsed.url} target="_blank" rel="noopener noreferrer" className="block">
                                              <img src={parsed.url} alt={parsed.filename} className="max-w-[200px] max-h-[150px] rounded-lg object-cover" />
                                            </a>
                                          );
                                        }
                                        return (
                                          <a key={`att-${i}`} href={parsed.url} target="_blank" rel="noopener noreferrer"
                                            className={`flex items-center gap-2 text-xs px-2 py-1 rounded-lg ${isOwnMessage ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-white text-[#5b7f95] hover:bg-gray-50'}`}>
                                            <FileText className="w-3 h-3 flex-shrink-0" />
                                            <span className="truncate">{parsed.filename || 'Download'}</span>
                                          </a>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>

                <div className="border-t border-gray-100">
                  {/* Attachment preview */}
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 px-4 pt-3">
                      {attachments.map((att, i) => (
                        <div key={`prev-${i}`} className="relative flex items-center gap-1.5 bg-gray-100 rounded-lg px-2 py-1 text-xs text-[#2d3748]">
                          {att.is_image ? <Image className="w-3 h-3 text-[#5b7f95]" /> : <FileText className="w-3 h-3 text-[#5b7f95]" />}
                          <span className="truncate max-w-[100px]">{att.filename}</span>
                          <button type="button" onClick={() => removeAttachment(i)} className="text-red-400 hover:text-red-600">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <form onSubmit={handleSendMessage} className="flex items-center gap-2 p-4">
                    {(user.is_admin || user.is_area_manager) && (
                      <label className="cursor-pointer flex-shrink-0" title="Attach files (max 5)">
                        <Paperclip className={`w-5 h-5 ${uploading ? 'text-gray-300 animate-pulse' : 'text-[#5b7f95] hover:text-[#4a6b80]'} transition-colors`} />
                        <input
                          type="file"
                          multiple
                          accept="image/jpeg,image/png,image/gif,image/webp,image/avif,.jpg,.jpeg,.png,.gif,.webp,.avif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                          onChange={handleAttachFiles}
                          className="hidden"
                          disabled={uploading || attachments.length >= 5}
                        />
                      </label>
                    )}
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 bg-gray-50 border-gray-200 rounded-xl"
                      data-testid="group-message-input"
                    />
                    <Button
                      type="submit"
                      disabled={sending || (!newMessage.trim() && attachments.length === 0)}
                      className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-xl px-6"
                      data-testid="send-group-message-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-[#64748b]">
                <div className="text-center">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium text-[#2d3748] mb-2">Select a Group</h3>
                  <p>Choose a group chat from the list to start messaging</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
